import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isFunction } from 'lodash'

import { colorShade, hexToRgb } from '../../../../utils/colors'
import { Keyboard } from '../../../../utils/constants'
import { ColorsPalette } from '../../../../config/colors'
import { getDisplacement } from '../utils'
import { OverlaySettings } from './OverlaySettings'
import { isNonEmptyArray, replaceBorderColor, replaceBorderSize, safeSpread, isObject, inEnum, isCtrlKeyPressed } from '../../../../utils'
import { px } from '../../../../utils/cssHelper'
import ResizeAnchor, { Positions } from './ResizeAnchor'

export const Types = Object.freeze({
  SOLID: 'solid',
  CLEAR: 'clear',
  TRANSLUCID: 'translucid'
})

export const Axes = Object.freeze({
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
  DIAGONAL: 'diagonal'
})

const MODE_RESIZE = 2
const MODE_DRAG = 1
const MODE_INACTIVE = 0

const MAT = 1
const TRANSPARENT = 0.7

const CONFIG = {
  style: { border: '2px solid darkgrey', borderRadius: '5px' },
  boundaries: { x: [0, 1], y: [0, 1] },
  overlay: new OverlaySettings(),
  keyboard: { canMove: true }
}

const ResizingDiv = ({
  config = CONFIG, content, data = { left: [1, 1], top: [0, 0], id: -1 }, isSelected = false, type = Types.CLEAR,
  onClick, onDragStop, onMaxIndexOverflow, onRightDisplacement, onDoubleClick, onIllegalDrag
}) => {
  const [initialLocation, setInitialLocation] = useState({ x: 0, y: 0 })
  const [initialIndexes, setInitalIndexes] = useState({ x: -1, y: -1 })
  const [indexes, setIndexes] = useState({ x: data.x, y: data.y })
  const [opacity, setOpacity] = useState(MAT)
  const [shouldDisplayActions, setShouldDisplayActions] = useState(false)
  const [mode, setMode] = useState(MODE_INACTIVE)

  const boundaries = useMemo(() => config.boundaries ?? CONFIG.boundaries, [config])
  const overlay = useMemo(() => (isObject(config.overlay) && config.overlay instanceof OverlaySettings)
    ? config.overlay.clone()
    : new OverlaySettings({ ...CONFIG.overlay, ...safeSpread(config?.overlay) })
  , [config])
  const configStyle = useMemo(() => ({ ...CONFIG.style, ...safeSpread(config?.style) }), [config])
  const dataConfig = useMemo(() => data.config ?? {}, [data.config])

  const draggableAxes = useMemo(() => isNonEmptyArray(dataConfig?.draggableAxes) ? dataConfig.draggableAxes.filter(inEnum(Axes)) : [], [dataConfig])
  const isHorizontallyLocked = useMemo(() => !(indexes.x[0] >= 0 && indexes.x[1] <= boundaries.x[1]), [indexes, boundaries.x])
  const isVerticallyDraggable = useMemo(() => draggableAxes.includes(Axes.VERTICAL), [draggableAxes])
  const isHorizontallyDraggable = useMemo(() => draggableAxes.includes(Axes.HORIZONTAL) && !isHorizontallyLocked, [draggableAxes, isHorizontallyLocked])
  const keyboardMoveEnabled = useMemo(() => config.keyboard?.canMove ?? true, [config.keyboard])

  const resizableAxes = useMemo(() => isNonEmptyArray(dataConfig?.resizableAxes) ? dataConfig.resizableAxes.filter(inEnum(Axes)) : [], [dataConfig])

  const offsetX = boundaries.x[0]
  const offsetY = boundaries.y[0]
  const isDraggable = useMemo(() => isVerticallyDraggable || isHorizontallyDraggable, [isVerticallyDraggable, isHorizontallyDraggable])
  const displacement = useMemo(() => config.displacement ?? CONFIG.displacement, [config])

  const alertRef = useRef(null)
  const moveRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    if (isSelected && containerRef.current) {
      containerRef.current.focus()
    }
  }, [isSelected, containerRef.current])

  useEffect(() => {
    let x = [0, 0]
    let y = [0, 0]

    if (data.x) {
      x = data.x
    }

    if (data.y) {
      y = data.y
    }

    setIndexes({ x, y })
  }, [data])

  const style = useMemo(() => {
    const limitXStart = boundaries.x[0] - offsetX
    const limitXEnd = boundaries.x[1]

    const x = [indexes.x[0] < limitXStart ? limitXStart : indexes.x[0], indexes.x[1] > limitXEnd ? limitXEnd : indexes.x[1]]
    const y = indexes.y

    const currentLeft = (displacement.x * (x[0] + offsetX))
    const currentTop = (displacement.y * (y[0] + offsetY))
    const height = px(displacement.y * (y[1] + 1 - y[0]) + 1)
    const width = px(displacement.x * (x[1] + 1 - x[0]) + 1)

    const { borderRadius } = configStyle
    let border = overlay.displayCustomColors && dataConfig.style?.borderColor ? replaceBorderColor(configStyle.border, dataConfig.style?.borderColor) : configStyle.border
    let backgroundColor
    let color

    if ([Types.CLEAR, Types.TRANSLUCID].includes(type)) {
      const background = type === Types.CLEAR ? ColorsPalette.WHITE : colorShade(dataConfig.style.backgroundColor, 75)

      backgroundColor = hexToRgb(background, 0.8)

      if (overlay.displayCustomColors && dataConfig.style?.backgroundColor) {
        border = replaceBorderColor(border, dataConfig.style.backgroundColor)
        color = colorShade(dataConfig.style.backgroundColor, -40)
      }
    } else {
      backgroundColor = overlay.displayCustomColors && dataConfig.style?.backgroundColor ? dataConfig.style.backgroundColor : ColorsPalette.GREEN
      color = dataConfig.style?.color ? colorShade(dataConfig.style.color, 20) : ColorsPalette.BLACK
    }

    const style = {
      left: currentLeft,
      top: currentTop,
      height,
      width,
      border,
      borderRadius,
      color,
      opacity,
      backgroundColor
    }

    if (isHorizontallyLocked) {
      style.borderRadius = '0px'
    }

    if (isSelected) {
      style.border = replaceBorderSize(style.border, '4px')
      style.border = replaceBorderColor(style.border, ColorsPalette.GREY)
    }

    return style
  }, [configStyle, indexes, opacity, overlay, dataConfig.style, isSelected, type, displacement, boundaries, isHorizontallyLocked])

  const updateData = useCallback((data, newIndexes) => {
    const newData = { ...data }

    if (data.x) {
      newData.x = newIndexes.x
    } else if (data.x) {
      newData.x = newIndexes.x
    }

    if (data.y) {
      newData.y = newIndexes.y
    } else if (data.y) {
      newData.y = newIndexes.y
    }

    return newData
  }, [])

  const getXDisplacement = useCallback(location => {
    return getDisplacement(initialLocation.x, location, displacement.x)
  }, [initialLocation.x, displacement.x])

  const getYDisplacement = useCallback(location => {
    return getDisplacement(initialLocation.y, location, displacement.y)
  }, [initialLocation.y, displacement.y])

  const triggerOverflow = useCallback(index => {
    if (index >= boundaries.y[1] && isFunction(onMaxIndexOverflow)) {
      onMaxIndexOverflow(index - boundaries.y[1] + 1)
    }
  }, [onMaxIndexOverflow, boundaries.y[1]])

  const moveLeft = useCallback((e, indexes) => {
    if (indexes === null) return null

    const index = indexes.x[0] + getXDisplacement(e.clientX)

    return index >= 0 && index <= indexes.x[1] ? { ...indexes, x: [index, indexes.x[1]] } : null
  }, [getXDisplacement])

  const moveRight = useCallback((e, indexes) => {
    if (indexes === null) return null

    const index = indexes.x[1] + getXDisplacement(e.clientX)

    return index <= boundaries.x[1] && index >= indexes.x[0] ? { ...indexes, x: [indexes.x[0], index] } : null
  }, [getXDisplacement])

  const moveTop = useCallback((e, indexes) => {
    if (indexes === null) return null

    const index = indexes.y[0] + getYDisplacement(e.clientY)

    return index >= 0 && index <= indexes.y[1] ? { ...indexes, y: [index, indexes.y[1]] } : null
  }, [getYDisplacement])

  const moveBottom = useCallback((e, indexes) => {
    if (indexes === null) return null

    const index = indexes.y[1] + getYDisplacement(e.clientY)

    if (index > boundaries.y[1]) {
      triggerOverflow(index)
    }

    return index >= indexes.y[0] ? { ...indexes, y: [indexes.y[0], index] } : null
  }, [triggerOverflow, getYDisplacement, boundaries.y[1]])

  const handleResize = useCallback(position => e => {
    e.stopPropagation()
    e.preventDefault()

    let indexes = null

    switch (position) {
      case Positions.LEFT:
        indexes = moveLeft(e, initialIndexes)
        break
      case Positions.RIGHT:
        indexes = moveRight(e, initialIndexes)
        break
      case Positions.BOTTOM:
        indexes = moveBottom(e, initialIndexes)
        break
      case Positions.TOP:
        indexes = moveTop(e, initialIndexes)
        break
      case Positions.TOP_LEFT:
        indexes = moveTop(e, moveLeft(e, initialIndexes))
        break
      case Positions.TOP_RIGHT:
        indexes = moveTop(e, moveRight(e, initialIndexes))
        break
      case Positions.BOTTOM_LEFT:
        indexes = moveBottom(e, moveLeft(e, initialIndexes))
        break
      case Positions.BOTTOM_RIGHT:
        indexes = moveBottom(e, moveRight(e, initialIndexes))
        break
    }

    if (indexes !== null) {
      setIndexes(indexes)
    }
  }, [moveLeft, moveRight, moveBottom, moveTop, initialIndexes])

  const handleDragStart = useCallback((e, mode = MODE_RESIZE) => {
    setInitialLocation({ x: e.clientX, y: e.clientY })
    setInitalIndexes(indexes)
    setOpacity(TRANSPARENT)
    setMode(mode)

    if (isFunction(onClick)) {
      onClick(e)
    }
  }, [data.id, indexes])

  const handleIllegalDrag = useCallback(() => {
    if (isFunction(onIllegalDrag)) {
      if (alertRef.current) {
        clearTimeout(alertRef.current)
      }

      alertRef.current = setTimeout(() => {
        onIllegalDrag()
      }, 500)
    }
  }, [alertRef.current, onIllegalDrag])

  const handleDragEnd = useCallback(e => {
    setInitialLocation({ x: 0, y: 0 })
    setInitalIndexes({ x: -1, y: -1 })
    setOpacity(MAT)
    setMode(MODE_INACTIVE)

    if (isFunction(onDragStop)) {
      onDragStop(updateData(data, indexes))
    }
  }, [updateData, indexes, data.id, onDragStop])

  const handleMove = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()

    if (e.clientX && e.clientY && initialLocation.x && initialLocation.y) {
      const newIndexes = { ...initialIndexes }
      const displacementX = getXDisplacement(e.clientX)

      if (isHorizontallyDraggable) {
        const startLeft = newIndexes.x[0] + displacementX
        newIndexes.x = [startLeft, startLeft <= 0 ? initialIndexes.x[1] - initialIndexes.x[0] : newIndexes.x[1] + displacementX]

        if (newIndexes.x[0] < 0) {
          newIndexes.x[0] = 0
        }

        if (newIndexes.x[1] > boundaries.x[1]) {
          newIndexes.x[1] = boundaries.x[1]
          newIndexes.x[0] = boundaries.x[1] - (initialIndexes.x[1] - initialIndexes.x[0])
        }
      } else if (displacementX > 0) {
        handleIllegalDrag()
      }

      if (isVerticallyDraggable) {
        const displacementY = getYDisplacement(e.clientY)

        const startTop = newIndexes.y[0] + displacementY
        newIndexes.y = [startTop, startTop <= 0 ? initialIndexes.y[1] - initialIndexes.y[0] : newIndexes.y[1] + displacementY]

        if (newIndexes.y[0] < 0) {
          newIndexes.y[0] = 0
        }
      }

      if (newIndexes.x[0] !== indexes.x[0] || newIndexes.y[0] !== indexes.y[0] || newIndexes.x[1] !== indexes.x[1] || newIndexes.y[1] !== indexes.y[1]) {
        setIndexes(newIndexes)
      }

      if (newIndexes.y[1] > boundaries.y[1]) {
        triggerOverflow(newIndexes.y[1])
      }
    }
  }, [boundaries.x, indexes, initialIndexes, isHorizontallyDraggable, isVerticallyDraggable, triggerOverflow, getXDisplacement, getYDisplacement, handleIllegalDrag])

  const saveMove = useCallback(indexes => {
    setIndexes(indexes)

    if (isFunction(onDragStop)) {
      if (moveRef.current) {
        clearTimeout(moveRef.current)
      }

      moveRef.current = setTimeout(() => {
        onDragStop(updateData(data, indexes))
      }, 1000)
    }
  }, [onDragStop, updateData, data, moveRef.current])

  const handleKeyDown = useCallback(e => {
    const newIndexes = { ...indexes }

    if (keyboardMoveEnabled) {
      if (e.key === Keyboard.ARROW_DOWN) {
        if (isVerticallyDraggable) {
          if (isCtrlKeyPressed(e)) {
            newIndexes.y = [newIndexes.y[0], newIndexes.y[1] + 1]
            triggerOverflow(newIndexes.y[1])
          } else if (e.shiftKey) {
            if (newIndexes.y[0] < newIndexes.y[1]) {
              const newIndex = newIndexes.y[1] - 1

              newIndexes.y = [newIndexes.y[0], newIndex >= 0 ? newIndex : 0]
            }
          } else {
            newIndexes.y = [newIndexes.y[0] + 1, newIndexes.y[1] + 1]
            triggerOverflow(newIndexes.y[1])
          }

          saveMove(newIndexes)
        }
      } else if (e.key === Keyboard.ARROW_UP) {
        if (isVerticallyDraggable) {
          if (isCtrlKeyPressed(e)) {
            const newIndex = newIndexes.y[0] - 1

            newIndexes.y = [newIndex >= 0 ? newIndex : 0, newIndexes.y[1]]
          } else if (e.shiftKey) {
            if (newIndexes.y[0] < newIndexes.y[1]) {
              newIndexes.y = [newIndexes.y[0] + 1, newIndexes.y[1]]
            }
          } else {
            const newIndex0 = newIndexes.y[0] - 1
            const newIndewx1 = newIndexes.y[1] - 1

            if (newIndex0 >= 0) {
              newIndexes.y = [newIndex0, newIndewx1]
            }
          }

          saveMove(newIndexes)
        }
      } else if (e.key === Keyboard.ARROW_LEFT) {
        if (isHorizontallyDraggable) {
          if (isCtrlKeyPressed(e)) {
            const newIndex = newIndexes.x[0] - 1

            newIndexes.x = [newIndex >= 0 ? newIndex : 0, newIndexes.x[1]]
          } else if (e.shiftKey) {
            if (newIndexes.x[0] < newIndexes.x[1]) {
              newIndexes.x = [newIndexes.x[0] + 1, newIndexes.x[1]]
            }
          } else {
            const newIndex0 = newIndexes.x[0] - 1
            const newIndewx1 = newIndexes.x[1] - 1

            if (newIndex0 >= 0) {
              newIndexes.x = [newIndex0, newIndewx1]
            }
          }

          saveMove(newIndexes)
        }
      } else if (e.key === Keyboard.ARROW_RIGHT) {
        if (isHorizontallyDraggable) {
          if (isCtrlKeyPressed(e)) {
            if (newIndexes.x[1] < boundaries.x[1]) {
              newIndexes.x = [newIndexes.x[0], newIndexes.x[1] + 1]
            }
          } else if (e.shiftKey) {
            if (newIndexes.x[0] < newIndexes.x[1]) {
              const newIndex = newIndexes.x[1] - 1

              newIndexes.x = [newIndexes.x[0], newIndex >= 0 ? newIndex : 0]
            }
          } else {
            if (newIndexes.x[1] < boundaries.x[1]) {
              newIndexes.x = [newIndexes.x[0] + 1, newIndexes.x[1] + 1]
            }
          }

          saveMove(newIndexes)
        }
      }
    }
  }, [boundaries.x, indexes, keyboardMoveEnabled, isHorizontallyDraggable, isVerticallyDraggable, triggerOverflow, saveMove])

  const handleDoubleClick = useCallback(() => {
    if (onDoubleClick && !dataConfig.disabled) {
      onDoubleClick(data)
    }
  }, [data, onDoubleClick])

  const draggableProps = useMemo(() => ({
    onDragEnd: handleDragEnd,
    onDragStart: handleDragStart,
    draggable: true
  }), [handleDragEnd, handleDragStart])

  const handleMouseEnter = useCallback(() => setShouldDisplayActions(true), [])
  const handleMouseLeave = useCallback(() => setShouldDisplayActions(false), [])

  const containerProps = useMemo(() => isDraggable
    ? { ...draggableProps, onDragStart: e => handleDragStart(e, MODE_DRAG), onDrag: handleMove }
    : {}
  , [isDraggable, draggableProps, handleMove])

  const shouldDisplayAnchors = useMemo(() => mode === MODE_RESIZE || (shouldDisplayActions && mode !== MODE_DRAG), [shouldDisplayActions, mode])

  const className = useMemo(() => {
    let zIndex

    if (!dataConfig.readonly) {
      zIndex = shouldDisplayAnchors || isSelected ? 80 : 50
    } else {
      zIndex = 'negative-1'
    }

    return `z-${zIndex}`
  }, [dataConfig.readonly, shouldDisplayAnchors, isSelected])

  const cellCallbacks = useMemo(() => {
    let callbacks = {}

    if (!dataConfig.readonly) {
      callbacks = {
        onClick,
        onDoubleClick: handleDoubleClick,
        onKeyDown: handleKeyDown,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave
      }
    }

    return callbacks
  }, [dataConfig.readonly, onClick, handleDoubleClick, handleKeyDown, handleMouseEnter, handleMouseLeave])

  return (
    <div
      id='resizable-div'
      className={className}
      ref={containerRef}
      style={style}
      tabIndex='0'
      {...cellCallbacks}
    >
      {shouldDisplayActions && isNonEmptyArray(dataConfig.actions) && (
        <div className='actions-container flex-row'>
          {dataConfig.actions}
        </div>
      )}
      {resizableAxes.includes(Axes.HORIZONTAL) && (
        <>
          <ResizeAnchor
            position={Positions.LEFT}
            display={isHorizontallyDraggable && shouldDisplayAnchors}
            left={-9}
            top='calc(50% - 9px)'
            draggableProps={draggableProps}
            onResize={handleResize}
          />
          <ResizeAnchor
            position={Positions.RIGHT}
            display={isHorizontallyDraggable && shouldDisplayAnchors}
            left='calc(100% - 5px)'
            top='calc(50% - 9px)'
            draggableProps={draggableProps}
            onResize={handleResize}
          />
        </>
      )}
      {resizableAxes.includes(Axes.VERTICAL) && (
        <>
          <ResizeAnchor
            position={Positions.TOP}
            display={!dataConfig.disabled && shouldDisplayAnchors}
            left='calc(50% - 8px)'
            top={-12}
            draggableProps={draggableProps}
            onResize={handleResize}
          />
          <ResizeAnchor
            position={Positions.BOTTOM}
            display={!dataConfig.disabled && shouldDisplayAnchors}
            left='calc(50% - 8px)'
            top='calc(100% - 8px)'
            draggableProps={draggableProps}
            onResize={handleResize}
          />
        </>
      )}
      {resizableAxes.includes(Axes.DIAGONAL) && (
        <>
          <ResizeAnchor
            position={Positions.TOP_LEFT}
            display={isHorizontallyDraggable && shouldDisplayAnchors}
            left={-9}
            top={-11}
            draggableProps={draggableProps}
            onResize={handleResize}
          />
          <ResizeAnchor
            position={Positions.TOP_RIGHT}
            display={isHorizontallyDraggable && shouldDisplayAnchors}
            left='calc(100% - 5px)'
            top={-11}
            draggableProps={draggableProps}
            onResize={handleResize}
          />
          <ResizeAnchor
            position={Positions.BOTTOM_RIGHT}
            display={isHorizontallyDraggable && shouldDisplayAnchors}
            left='calc(100% - 5px)'
            top='calc(100% - 9px)'
            draggableProps={draggableProps}
            onResize={handleResize}
          />
          <ResizeAnchor
            position={Positions.BOTTOM_LEFT}
            display={isHorizontallyDraggable && shouldDisplayAnchors}
            left={-9}
            top='calc(100% - 9px)'
            draggableProps={draggableProps}
            onResize={handleResize}
          />
        </>
      )}
      <div className={`side-container h-100 w-100${isDraggable ? ' hover-cu-mv' : ''}`} {...containerProps}>
        {content}
      </div>
    </div>
  )
}

export default ResizingDiv
